import * as Yup from "yup";

// Core Regex Patterns representing Industry Standards globally natively explicitly.
const phoneRegExp = /^[6-9]\d{9}$/;
const panRegExp = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
const gstRegExp = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const aadharRegExp = /^\d{12}$/;
const nameRegExp = /^[a-zA-Z\s]*$/;
const pincodeRegExp = /^[1-9][0-9]{5}$/;

export const validationConstants = {
  mobile: Yup.string()
    .required("Mobile number is required.")
    .matches(phoneRegExp, "Enter a valid 10-digit Indian mobile number."),
    
  optionalMobile: Yup.string()
    .nullable()
    .test(
        "is-valid-optional-mobile",
        "Enter a valid 10-digit Indian mobile number.",
        (val) => !val || phoneRegExp.test(val)
    ),

  email: Yup.string()
    .trim()
    .email("Enter a valid email address.")
    .required("Email is required."),

  optionalEmail: Yup.string().trim().email("Enter a valid email address.").nullable(),

  name: Yup.string()
    .trim()
    .required("Name is required.")
    .matches(nameRegExp, "Name can only contain letters and spaces.")
    .min(2, "Name must be at least 2 characters.")
    .max(50, "Name cannot exceed 50 characters."),

  pan: Yup.string()
    .required("PAN Card is required.")
    .matches(panRegExp, "Enter a valid 10-character PAN number."),

  gst: Yup.string()
    .required("GST Number is required.")
    .matches(gstRegExp, "Enter a valid 15-character GST number format."),

  aadhar: Yup.string()
    .required("Aadhar number is required.")
    .matches(aadharRegExp, "Enter a valid 12-digit Aadhar number."),

  pincode: Yup.string()
    .required("Pincode is required.")
    .matches(pincodeRegExp, "Enter a valid 6-digit Pincode."),
    
  password: Yup.string()
    .required("Password is required.")
    .min(8, "Password must be at least 8 characters.")
    .matches(/[A-Z]/, "Password must contain at least one uppercase letter.")
    .matches(/[a-z]/, "Password must contain at least one lowercase letter.")
    .matches(/[0-9]/, "Password must contain at least one number.")
    .matches(/[\W_]/, "Password must contain at least one special character."),
    
  requiredString: (fieldName = "This field") => Yup.string().trim().required(`${fieldName} is required.`),

  // Numeric field validators - Industry standard
  positiveNumber: (fieldName = "Value", options = {}) => {
    const { required = true, min = 0, max = null, decimalPlaces = null } = options;
    let schema = Yup.number();
    
    if (required) {
      schema = schema.required(`${fieldName} is required.`);
    } else {
      schema = schema.nullable();
    }
    
    schema = schema
      .typeError(`${fieldName} must be a number.`)
      .min(min, `${fieldName} must be at least ${min}.`);
    
    if (max !== null) {
      schema = schema.max(max, `${fieldName} cannot exceed ${max}.`);
    }
    
    if (decimalPlaces !== null) {
      schema = schema.test(
        "decimal-places",
        `${fieldName} can have maximum ${decimalPlaces} decimal places.`,
        (val) => !val || /^\d+(\.\d{1,})/.test(val.toString()) === false || val.toString().split(".")[1]?.length <= decimalPlaces
      );
    }
    
    return schema;
  },

  percentage: (fieldName = "Percentage", options = {}) => {
    const { required = true, minPercent = 0, maxPercent = 100, decimalPlaces = 2 } = options;
    let schema = Yup.number();
    
    if (required) {
      schema = schema.required(`${fieldName} is required.`);
    } else {
      schema = schema.nullable();
    }
    
    schema = schema
      .typeError(`${fieldName} must be a number.`)
      .min(minPercent, `${fieldName} must be at least ${minPercent}%.`)
      .max(maxPercent, `${fieldName} cannot exceed ${maxPercent}%.`)
      .test(
        "decimal-places",
        `${fieldName} can have maximum ${decimalPlaces} decimal places.`,
        (val) => !val || val.toString().split(".")[1]?.length <= decimalPlaces
      );
    
    return schema;
  },

  currency: (fieldName = "Amount", options = {}) => {
    const { required = true, min = 0, max = null, decimalPlaces = 2 } = options;
    let schema = Yup.number();
    
    if (required) {
      schema = schema.required(`${fieldName} is required.`);
    } else {
      schema = schema.nullable();
    }
    
    schema = schema
      .typeError(`${fieldName} must be a valid number.`)
      .min(min, `${fieldName} must be at least ₹${min}.`);
    
    if (max !== null) {
      schema = schema.max(max, `${fieldName} cannot exceed ₹${max}.`);
    }
    
    schema = schema.test(
      "decimal-places",
      `${fieldName} can have maximum ${decimalPlaces} decimal places.`,
      (val) => !val || val.toString().split(".")[1]?.length <= decimalPlaces
    );
    
    return schema;
  },
};

/**
 * Validates a generic data object precisely against a supplied Yup generic schema asynchronously effortlessly.
 * @param {Yup.ObjectSchema} schema The Yup explicit validation schema map wrapping variables safely.
 * @param {Object} data The standard local generic data to validate natively safely against the rules cleanly.
 * @returns {Promise<Object>} Promisified Object defining local `{field: errorText}` gracefully resolving mapping safely natively.
 */
export const validateFormAsync = async (schema, data) => {
  try {
    await schema.validate(data, { abortEarly: false });
    return {};
  } catch (err) {
    const localErrors = {};
    if (err.inner) {
      err.inner.forEach((error) => {
        if (error.path && !localErrors[error.path]) {
          localErrors[error.path] = error.message;
        }
      });
    }
    return localErrors;
  }
};

/**
 * Validates a generic data object precisely against a supplied Yup generic schema synchronously rapidly.
 * Useful for handling quick onSubmit evaluations where Promises aren't entirely needed explicitly reliably perfectly.
 */
export const validateFormSync = (schema, data) => {
  try {
    schema.validateSync(data, { abortEarly: false });
    return {};
  } catch (err) {
    const localErrors = {};
    if (err.inner) {
      err.inner.forEach((error) => {
        if (error.path && !localErrors[error.path]) {
          localErrors[error.path] = error.message;
        }
      });
    }
    return localErrors;
  }
};

/**
 * Specifically validates geographical location strings evaluating strict boundaries catching spam characters locally safely natively.
 */
export const isValidLocationString = (val) => {
  if (!val || typeof val !== 'string') return false;
  const trimmed = val.trim();
  if (trimmed.length < 3) return false;
  if (trimmed.length > 150) return false;
  
  // Reject 5+ consecutive identical characters inherently blocking spam (e.g., 'kkkkk')
  if (/(.)\1{4,}/.test(trimmed)) return false;
  
  // Must contain at least one letter cleanly natively
  if (!/[a-zA-Z]/.test(trimmed)) return false;
  
  return true;
};
